import axios from 'axios';
import {
  makeProxyRequest,
  makeProxyRequestWithExpectedErrors,
  makeRequest,
  makeRequestWithoutBaseUrl,
  makeRetryProxyRequest,
} from '../utils/request';
import { apiV1, apiV2, rpc, gateways } from './helper';
import { httpMethods, httpStatusCodes } from '../constants/Http';
import { prepareSolutionPayload, TIMEOUT_VALUE } from './utils';
import { videoSolution } from '../components/ProblemTypes/Video/constants';
import { audioSolution } from '../components/ProblemTypes/Audio/constants';
import {
  BLOB,
  BLOB_PATH_TYPE,
  TREE,
  TREE_PATH_TYPE,
} from '../constants/UixConstants';
import { OPTIMIZED_SHORT_TYPE_PROBLEMS } from '../constants/ProblemTypes';

export const getServerTimeAndTrackingId = () =>
  makeRequest('get', rpc('/get_server_time'));

export const getTestBySlug = slug =>
  makeProxyRequest('get', apiV1(`/test/${slug}?is_test_env=true`), true);

export const getApiV1SolutionSet = (userId, userType, testSlug) => {
  return makeProxyRequestWithExpectedErrors({
    expectedError: httpStatusCodes.NOT_FOUND,
    method: 'get',
    url: apiV1(
      `/${userType}/${userId}/solutionset?context=test&context_slug=${testSlug}`
    ),
    appendUserName: true,
  });
};

export const initTest = (userId, userType, testId, testSlug) => {
  return getApiV1SolutionSet(userId, userType, testSlug)
    .then(resp => {
      return {
        assessmentId: resp.data.id,
        assessmentType: 'test',
        isExtendedTest: resp.data.add_test_duration > 0,
        websocketEventSequence: resp.data?.event_sequence,
      };
    })
    .catch(async error => {
      if (error.response) {
        const { response } = error;
        if (
          response.status === httpStatusCodes.NOT_FOUND &&
          response.config.method === httpMethods.GET
        ) {
          const initTestResponse = await makeProxyRequest(
            'post',
            rpc(`/test.init/${testId}`),
            true,
            {
              id: testId,
            }
          );
          return {
            assessmentId: initTestResponse.data.testsolutionset_id,
            assessmentType: 'test',
            isExtendedTest: false,
            websocketEventSequence: false,
          };
        }
      }
      throw error;
    });
};

export const startTest = testId =>
  makeProxyRequest('post', rpc(`/test.start/${testId}`), true, {
    id: testId,
  });

export const getTestSolutionSetByTestId = testId =>
  makeProxyRequest('get', apiV2(`/assessment/${testId}/solutionset`));

export const getSectionWiseProblems = (testId, testSlug) =>
  makeProxyRequest(
    'get',
    apiV2(`/assessment/${testId}/sectionwise/problems?test_slug=${testSlug}`),
    false
  );

export const getProblemsByIds = (testId, problemIds) => {
  const ids = (problemIds || []).join(',');
  return makeProxyRequest(
    'get',
    apiV2(`/assessment/${testId}/problems?ids=${ids}`)
  );
};

export const getHacker = hackerId =>
  makeProxyRequest('get', apiV1(`/hacker/${hackerId}`));

export const getInvite = inviteId =>
  makeProxyRequest('get', apiV1(`/invite/${inviteId}`));

export const getProblemSolution = (
  userId,
  userType,
  testSlug,
  problemSlug,
  context = 'test',
  solutionSlug = ''
) =>
  makeProxyRequestWithExpectedErrors({
    expectedError: httpStatusCodes.NOT_FOUND,
    method: 'get',
    url: apiV1(
      `/${userType}/${userId}/solution?context=${context}&context_slug=${testSlug}&problem_slug=${problemSlug}&solution_slug=${solutionSlug}`
    ),
    appendUserName: true,
  });

export const updateSolution = (
  solution,
  solutionSlug,
  retryCallback = () => {}
) =>
  makeRetryProxyRequest(
    'patch',
    apiV1(`/solution/${solutionSlug}`),
    true,
    solution,
    retryCallback,
    {
      timeout: TIMEOUT_VALUE,
    }
  );

export const clearAnswer = (solutionSlug, testSlug) =>
  makeProxyRequest(
    'delete',
    apiV1(`/solution/${solutionSlug}?context_slug=${testSlug}`),
    true
  );

export const saveSolution = solution =>
  makeProxyRequest('post', apiV1(`/solution`), true, solution);

export const saveOrUpdateProblemSolution = (
  userId,
  userType,
  testSlug,
  problemSlug,
  solution,
  solutionSlug,
  context = 'test',
  retryCallback
) => {
  if (solutionSlug) {
    const solutionPayload = prepareSolutionPayload(
      solution,
      'update',
      testSlug
    );
    return updateSolution(solutionPayload, solutionSlug, retryCallback);
  }
  if (OPTIMIZED_SHORT_TYPE_PROBLEMS[solution.solution_type] && !solutionSlug) {
    const solutionPayload = prepareSolutionPayload(solution, 'save', testSlug);
    return saveSolution(solutionPayload);
  }
  return getProblemSolution(
    userId,
    userType,
    testSlug,
    problemSlug,
    context,
    solutionSlug
  )
    .then(res => {
      const newSolution = { ...res.data, ...solution };
      const solutionPayload = prepareSolutionPayload(
        newSolution,
        'update',
        testSlug
      );
      return updateSolution(solutionPayload, res.data.slug, retryCallback);
    })
    .catch(error => {
      if (error.response) {
        const { response } = error;
        if (
          response.status === httpStatusCodes.NOT_FOUND &&
          response.config.method === httpMethods.GET
        ) {
          const solutionPayload = prepareSolutionPayload(
            solution,
            'save',
            testSlug
          );
          return saveSolution(solutionPayload);
        }
      }
      throw error;
    });
};

export const submitTestApi = ({
  testSolutionSetId,
  submissionType,
  proctoringSubmitTime,
  reason,
  testSolutionSetEndTime,
  websocketEventSequence,
  failedSocketEvents,
}) =>
  makeProxyRequest('post', rpc(`/test.submit_all/${testSolutionSetId}`), true, {
    submission_type: submissionType,
    proctoring_end_time: proctoringSubmitTime,
    reason,
    testsolutionset_end_time: testSolutionSetEndTime,
    websocket_event_sequence: websocketEventSequence,
    failed_socket_events: failedSocketEvents,
  });

export const submitSectionApi = payload =>
  makeProxyRequest('post', rpc(`/test.submit_section`), true, payload);
export const continueTest = (test_slug, invite_id) =>
  makeProxyRequest('post', rpc(`/test.restart`), true, {
    invite_id,
    test_slug,
  });
export const sumbitFeedbackApi = payload =>
  makeProxyRequest('post', rpc(`/feedback.collect`), true, payload);
export const getFeedbackApi = testSlug =>
  makeProxyRequestWithExpectedErrors({
    expectedError: httpStatusCodes.NOT_FOUND,
    method: 'head',
    url: rpc(`/feedback.collect?source=PT&test_slug=${testSlug}`),
    appendUserName: true,
  });
export const getUserTypeApi = () =>
  makeProxyRequest('get', gateways('/identity'));

export const validateSession = () =>
  makeProxyRequest('get', gateways('/validate-session'));

export const getRecruiterDetails = userid =>
  makeProxyRequest('get', apiV1(`/recruiter/${userid}`), true);
export const getHackerDetails = userid =>
  makeProxyRequest('get', apiV1(`/hacker/${userid}`), true);

export const uploadSubjectiveSolutionAttachment = payload =>
  makeProxyRequest('post', rpc(`/upload`), true, payload);

export const getReportSolutionSetByTestSlug = (testSlug, userName) =>
  makeProxyRequest(
    'get',
    apiV1(`/test/${testSlug}/solutionset?username=${userName}`),
    true
  );

export const retakeTestApi = id =>
  makeProxyRequest('post', rpc(`/test.retake/${id}`), true, { id });

export const getReportSolutionBySolutionSlug = (
  solutionSlug,
  source = '',
  testSlug
) =>
  makeProxyRequest(
    'get',
    apiV1(`/solution/${solutionSlug}?source=${source}&test_slug=${testSlug}`),
    true
  );

export const reportIssueApi = payload =>
  makeProxyRequest('post', rpc('/report_issue'), true, payload);

export const prepareUrlForIframe = url =>
  makeRequestWithoutBaseUrl('head', url);

export const checkPodHealth = healthCheckUrl =>
  makeRequestWithoutBaseUrl('get', healthCheckUrl);

export const fetchRepo = (path, solutionSlug, fileType) => {
  let pathType;
  if (fileType === TREE) {
    pathType = TREE_PATH_TYPE;
  } else if (fileType === BLOB) {
    pathType = BLOB_PATH_TYPE;
  }

  return makeProxyRequest(
    'get',
    apiV1(`/solution/${solutionSlug}/${pathType}?path=${path}`),
    true
  );
};

export const getJupyterPodStatus = url =>
  makeProxyRequest('get', rpc(url), true);
export const createJupyterPod = url =>
  makeProxyRequestWithExpectedErrors({
    expectedError: httpStatusCodes.BAD_REQUEST,
    method: 'post',
    url: rpc(url),
    appendUserName: true,
  });

export const createJupyterPodbyProblemSlug = ({
  problemSlug,
  solutionSlug,
  editingfolder,
}) => {
  const url = `/create_server/djp/${problemSlug}`;
  return makeProxyRequest('POST', rpc(url), true, {
    solution_slug: solutionSlug,
    is_open_editor: !!editingfolder,
    editor_type: editingfolder,
  });
};

export const getJupyerPodStatusbySolutionSlug = solutionSlug => {
  const url = `/get_server/djp/${solutionSlug}`;
  return makeProxyRequest('GET', rpc(url), true);
};

export const getProblemsBySlug = arg => {
  const { problemslug, userRoleType, testSlug = '' } = arg;
  let queryParams = userRoleType ? `?_role=${userRoleType}` : '';
  queryParams = testSlug
    ? `${queryParams ? '&' : '?'}test_slug=${testSlug}`
    : queryParams;
  return makeProxyRequest(
    'get',
    apiV1(`/problem/${problemslug}${queryParams}`)
  );
};

export const videoUploadApi = (videoPutUrl, videoBlob, progressBar) => {
  const headers = {
    'Content-Type': videoSolution.UPLOAD_MEDIA_TYPE,
  };
  const onUploadProgress = progressEvent => {
    const { loaded, total } = progressEvent;
    if (progressEvent.lengthComputable) {
      const percentComplete = loaded / total;
      if (progressBar) progressBar(percentComplete);
    }
  };
  return axios.put(videoPutUrl, videoBlob, { headers, onUploadProgress });
};

export const audioUploadApi = (audioPutUrl, audioBlob) => {
  const headers = {
    'Content-Type': audioSolution.MEDIA_TYPE,
  };
  return axios.put(audioPutUrl, audioBlob, { headers });
};

export const getAllRevisions = solutionSlug =>
  makeProxyRequest('get', apiV1(`/solution/${solutionSlug}/revisions`), true);

export const getRevision = id =>
  makeProxyRequest('get', apiV1(`/solutionrevision/${id}`), true);
